import './AmcNtbLtv.scss';
import { useEffect, useMemo } from 'react';
import { useSetError } from '@src/redux/modals';
import { Channel4, Channel5, Channel6 } from '@src/utils/colors';
import { toPrettyNumber, toPrettySpend } from '@src/utils/performance-utils';
import { LineChart } from '@Components/Charts/LineChart';
import { ChartContainer } from '@Components/ChartContainer';
import { DataCard } from '@Components/DataCard';
import { Spinner } from '@Components/Spinner/Spinner';
import { BPMTable } from '@Components/StickyTable/BPMTable';
import { TableEmptyState } from '@Components/TableEmptyState';
import {
  COHORT_LTV_TABLE_HEADERS,
  DELIVERY_METRICS_COHORT_TABLE_HEADERS,
  MAX_MONTH_OFFSET
} from './amc-ntb-ltv-config';
import { AmcNtbLtvResponse, BLENDED_LTV_BAN_MONTH_OFFSET } from './amc-ntb-ltv-types';
import {
  formatShortMonth,
  getBlendedLtvAtMonthOffset,
  pivotCohortLtvRows,
  summarizeL12MDeliveryMetrics,
  withDeliveryMetricsCalculatedFields
} from './amc-ntb-ltv-utils';
import { useAmcNtbLtvData } from './utils/useAmcNtbLtvData';

const NoTableData = () => <TableEmptyState message='No data to show' />;

interface OverviewCardRowProps {
  data: AmcNtbLtvResponse;
}

const OverviewCardRow = ({ data }: OverviewCardRowProps) => {
  const overview = summarizeL12MDeliveryMetrics(data.deliveryMetrics);
  const blendedLtv = getBlendedLtvAtMonthOffset(data.blendedLtv, BLENDED_LTV_BAN_MONTH_OFFSET);

  return (
    <div className='data-card-row'>
      <DataCard
        label='L12M New To Brand Customers'
        value={toPrettyNumber(overview.ntbCustomers)}
        width='100%'
      />
      <DataCard
        label='Cost per NTB Acquisition'
        value={
          overview.costPerNtbAcquisition == null
            ? '—'
            : toPrettySpend(overview.costPerNtbAcquisition, 2)
        }
        width='100%'
      />
      <DataCard
        label='NTB Average Order Value'
        value={
          overview.ntbAverageOrderValue == null
            ? '—'
            : toPrettySpend(overview.ntbAverageOrderValue, 2)
        }
        width='100%'
      />
      <DataCard
        label='Blended LTV'
        value={blendedLtv == null ? '—' : toPrettySpend(blendedLtv, 2)}
        width='100%'
      />
    </div>
  );
};

interface AmcNtbLtvProps {
  amcInstanceId?: string;
}

export const AmcNtbLtv = ({ amcInstanceId }: AmcNtbLtvProps) => {
  const { data, isLoading, isFetching, error } = useAmcNtbLtvData(amcInstanceId);
  const setError = useSetError();

  useEffect(() => {
    if (error) {
      setError({
        title: 'Error fetching NTB Performance and LTV data',
        message:
          'There was an error fetching the NTB performance and long-term value data. Please try again later.',
        reportError: error
      });
    }
  }, [error, setError]);

  const isBusy = isFetching || isLoading;

  const deliveryMetricsWithMetrics = useMemo(
    () => (data?.deliveryMetrics ?? []).map(withDeliveryMetricsCalculatedFields),
    [data]
  );

  // deliveryMetrics is sorted most-recent month first (see AmcNtbLtvResponse), matching how the
  // delivery-metrics table below reads; the CPA/AOV trend line reads left-to-right
  // chronologically, so it needs the reverse order.
  const cpaAovChartData = useMemo(
    () => [...deliveryMetricsWithMetrics].reverse(),
    [deliveryMetricsWithMetrics]
  );

  const cohortLtvMatrix = useMemo(
    () => pivotCohortLtvRows(data?.cohortLtv ?? [], MAX_MONTH_OFFSET),
    [data]
  );

  return (
    <div className='amc-ntb-ltv'>
      {isBusy ? (
        <div className='amc-ntb-ltv-cards-loading'>
          <Spinner />
        </div>
      ) : (
        data && <OverviewCardRow data={data} />
      )}

      <div className='amc-ntb-ltv-row'>
        <ChartContainer
          title='Blended Longterm Value & AOV by Month'
          isLoading={isBusy}
          minHeight={300}
        >
          <LineChart
            data={data?.blendedLtv ?? []}
            xAxisDataKey='monthOffset'
            xAxisLabel='Months Since Acquisition'
            customTooltipHeader={value => `Month ${value}`}
            lineDataKeys={[
              { dataKey: 'monthOffsetAov', name: 'Average Order Value' },
              { dataKey: 'monthOffsetCumulativeLtv', name: 'Blended LTV' }
            ]}
            colorMappings={[
              { name: 'Average Order Value', color: Channel6 },
              { name: 'Blended LTV', color: Channel5 }
            ]}
            useLeftYAxis
            yAxisTickFormatter={value => toPrettySpend(value, 0)}
            tooltipFormatter={value => toPrettySpend(value, 2)}
          />
        </ChartContainer>
        <ChartContainer
          title='New to Brand CPA + Avg. Order Value by Month'
          isLoading={isBusy}
          minHeight={300}
        >
          <LineChart
            data={cpaAovChartData}
            xAxisDataKey='cohortMonth'
            xAxisTickFormatter={formatShortMonth}
            customTooltipHeader={formatShortMonth}
            lineDataKeys={[
              { dataKey: 'costPerNtbAcquisition', name: 'Cost per NTB Acquisition' },
              { dataKey: 'ntbAverageOrderValue', name: 'NTB Average Order Value' }
            ]}
            colorMappings={[
              { name: 'Cost per NTB Acquisition', color: Channel6 },
              { name: 'NTB Average Order Value', color: Channel4 }
            ]}
            useLeftYAxis
            yAxisTickFormatter={value => toPrettySpend(value, 0)}
            tooltipFormatter={value => toPrettySpend(value, 2)}
          />
        </ChartContainer>
      </div>

      <div className='amc-ntb-ltv-row'>
        <ChartContainer
          title='NTB Delivery Metrics by Acquisition Cohort'
          isLoading={isBusy}
          minHeight={500}
        >
          <div className='amc-ntb-ltv-table-wrapper'>
            <BPMTable
              data={deliveryMetricsWithMetrics}
              filterBar={false}
              headers={DELIVERY_METRICS_COHORT_TABLE_HEADERS}
              noRowsRenderer={NoTableData}
            />
          </div>
        </ChartContainer>
        <ChartContainer
          title='Cumulative LTV by Cohort and Month Since Acquisition'
          isLoading={isBusy}
          minHeight={500}
        >
          <div className='amc-ntb-ltv-table-wrapper'>
            <BPMTable
              data={cohortLtvMatrix}
              filterBar={false}
              headers={COHORT_LTV_TABLE_HEADERS}
              noRowsRenderer={NoTableData}
            />
          </div>
        </ChartContainer>
      </div>
    </div>
  );
};
